import { useMemo, useState } from "react";
import { Plus } from "lucide-react";

import { Button, DataLoadError } from "components/ui";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import {
  useDeleteVaccineRefusal,
  usePatientVaccineRefusals,
  useWithdrawVaccineRefusal,
} from "features/immunizations/hooks/useVaccineRefusals";
import {
  groupRefusals,
  isClinicalNonAdministration,
  refusalOutcomeLabel,
} from "features/immunizations/utils/vaccineRefusal";
import { formatDateWithDashes } from "utils/dateFormatter";

import { RecordRefusalDialog } from "./RecordRefusalDialog";
import s from "./RefusalsTab.module.css";

const RefusalRow = ({ refusal, onWithdraw, onDelete }) => (
  <li className={s.row}>
    <div className={s.rowMain}>
      <span className={s.vaccine}>{refusal.vaccineName}</span>
      <span
        className={`${s.outcome} ${
          isClinicalNonAdministration(refusal) ? s.outcomeClinical : ""
        }`}
      >
        {refusalOutcomeLabel(refusal.outcome)}
      </span>
      {!!refusal.withdrawn && (
        <span className={s.withdrawnBadge}>Withdrawn</span>
      )}
    </div>
    <div className={s.rowMeta}>
      <span>{formatDateWithDashes(refusal.refusedOn)}</span>
      <span>{refusal.refusalReasonDescription}</span>
    </div>
    {!!refusal.notes && <p className={s.notes}>{refusal.notes}</p>}
    <div className={s.rowActions}>
      {!refusal.withdrawn && (
        <button
          type="button"
          className={s.linkButton}
          onClick={() => onWithdraw(refusal)}
        >
          Patient changed their mind
        </button>
      )}
      <button
        type="button"
        className={`${s.linkButton} ${s.linkButtonDanger}`}
        onClick={() => onDelete(refusal)}
      >
        Recorded in error
      </button>
    </div>
  </li>
);

/**
 * Vaccines this patient was not given, and why (KIN-1849).
 *
 * A declined dose reads exactly like one never offered, so the clinician re-asks at every
 * visit and the gap in the schedule is never explained. These are kept out of the history
 * table rather than merged into it: a refusal has no batch, route, site or administering
 * clinician, so it cannot honestly fill those columns.
 *
 * Withdrawal and deletion are deliberately different actions. Withdrawing says the patient
 * reconsidered — the record stays, and only its effect on what is outstanding stops, which
 * is the ordinary path for a parent who declines RSV in the maternity setting and opts in
 * later at the GP. Deleting says the record should never have existed.
 */
export const RefusalsTab = ({ patientId }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);

  const {
    data: refusals,
    isLoading,
    isError,
    refetch,
  } = usePatientVaccineRefusals(patientId);

  const { mutate: withdrawRefusal, isPending: isWithdrawing } =
    useWithdrawVaccineRefusal();
  const { mutate: deleteRefusal, isPending: isDeleting } =
    useDeleteVaccineRefusal();

  const { standing, withdrawn } = useMemo(
    () => groupRefusals(refusals),
    [refusals]
  );

  if (isError) {
    return <DataLoadError onRetry={refetch} />;
  }

  const hasNone = !isLoading && !standing.length && !withdrawn.length;

  return (
    <div className={s.container}>
      <div className={s.header}>
        <h6 className={s.title}>Vaccines not given</h6>
        <Button
          variant="primary"
          endIcon={Plus}
          onClick={() => setIsRecording(true)}
        >
          Record
        </Button>
      </div>

      {isLoading && <p className={s.muted}>Loading...</p>}

      {hasNone && (
        <p className={s.muted}>
          Nothing recorded. A vaccine declined or withheld is recorded here so the
          gap in the schedule is explained rather than re-asked at every visit.
        </p>
      )}

      {!!standing.length && (
        <ul className={s.list}>
          {standing.map((refusal) => (
            <RefusalRow
              key={refusal.id}
              refusal={refusal}
              onWithdraw={setPendingWithdrawal}
              onDelete={setPendingDeletion}
            />
          ))}
        </ul>
      )}

      {!!withdrawn.length && (
        <>
          <h6 className={s.subheading}>No longer applies</h6>
          <ul className={s.list}>
            {withdrawn.map((refusal) => (
              <RefusalRow
                key={refusal.id}
                refusal={refusal}
                onWithdraw={setPendingWithdrawal}
                onDelete={setPendingDeletion}
              />
            ))}
          </ul>
        </>
      )}

      <RecordRefusalDialog
        open={isRecording}
        onClose={() => setIsRecording(false)}
        patientId={patientId}
      />

      <ConfirmDialog
        isOpen={!!pendingWithdrawal}
        onClose={() => setPendingWithdrawal(null)}
        onConfirm={() =>
          withdrawRefusal(pendingWithdrawal.id, {
            onSuccess: () => setPendingWithdrawal(null),
          })
        }
        isLoading={isWithdrawing}
        title="Patient changed their mind?"
        message={`${pendingWithdrawal?.vaccineName} becomes outstanding again. The record of it having been declined stays.`}
        confirmLabel="Withdraw"
      />

      <ConfirmDialog
        isOpen={!!pendingDeletion}
        onClose={() => setPendingDeletion(null)}
        onConfirm={() =>
          deleteRefusal(pendingDeletion.id, {
            onSuccess: () => setPendingDeletion(null),
          })
        }
        isLoading={isDeleting}
        title="Recorded in error?"
        message={`This removes the record of ${pendingDeletion?.vaccineName} entirely. If the patient simply changed their mind, withdraw it instead so the history is kept.`}
        confirmLabel="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};
